import { Square } from "chess.js";
import { Dispatch, SetStateAction } from "react";
import {
    leaveMenuIcons,
    moveRemoteDownInGameOverMenu,
    moveRemoteDownInMenuIcons,
    moveRemoteUpInGameOverMenu,
    moveRemoteUpInMenuIcons,
    toggleInGameMenu,
} from "../../../utils/navigationHelpers";
import { playSelectAudioStandalone } from "../../../hooks/useSoundEffect";
import { handleHome, undoLastMove, restartGame } from "../../../utils/gameControls";
import { getDownSquare, getLeftSquare, getRightSquare, getUpSquare } from "../../../utils/utils";
import { RemoteNavigationState, GameState } from "../../../context/gameContextTypes";
import { MultiplayerState } from "../../../context/MultiplayerContext";

interface HandleBoardNavigationParams {
    isLeft: boolean;
    isRight: boolean;
    isUp: boolean;
    isDown: boolean;
    isEnter: boolean;
    remoteNavigation: RemoteNavigationState;
    multiplayerState: MultiplayerState;
    gameState: GameState;
    setGameState: Dispatch<SetStateAction<GameState>>;
    setRemoteNavigation: Dispatch<SetStateAction<RemoteNavigationState>>;
    setMultiplayerState: Dispatch<SetStateAction<MultiplayerState>>;
    onSquareClick: (square: Square) => void;
    allowMove: () => boolean;
    setHoveredSquare: (square: Square | null) => void;
}

export function handleMenuIconNavigation(
    isLeft: boolean,
    isUp: boolean,
    isDown: boolean,
    isEnter: boolean,
    remoteNavigation: RemoteNavigationState,
    gameState: GameState,
    setGameState: Dispatch<SetStateAction<GameState>>,
    setRemoteNavigation: Dispatch<SetStateAction<RemoteNavigationState>>,
) {
    if (isLeft) {
        leaveMenuIcons(setRemoteNavigation);
    } else if (isDown) {
        moveRemoteDownInMenuIcons(setRemoteNavigation, gameState.isAIGame);
    } else if (isUp) {
        moveRemoteUpInMenuIcons(setRemoteNavigation, gameState.isAIGame);
    } else if (isEnter) {
        if (remoteNavigation.selectedMenuIcon === "Undo") {
            playSelectAudioStandalone();
            undoLastMove(setGameState);
        } else if (remoteNavigation.selectedMenuIcon === "Options") {
            playSelectAudioStandalone();
            toggleInGameMenu(setGameState);
        }
    }
}

export function handleGameOverMenuNavigation(
    isUp: boolean,
    isDown: boolean,
    isEnter: boolean,
    remoteNavigation: RemoteNavigationState,
    setGameState: Dispatch<SetStateAction<GameState>>,
    setMultiplayerState: Dispatch<SetStateAction<MultiplayerState>>,
    setRemoteNavigation: Dispatch<SetStateAction<RemoteNavigationState>>,
) {
    if (!remoteNavigation.isInGameOverMenu) return;

    if (isDown) {
        moveRemoteDownInGameOverMenu(setRemoteNavigation);
    } else if (isUp) {
        moveRemoteUpInGameOverMenu(setRemoteNavigation);
    } else if (isEnter) {
        if (remoteNavigation.selectedGameOverIcon === "Play Again") {
            restartGame(setGameState);
        } else if (remoteNavigation.selectedGameOverIcon === "Main Menu") {
            handleHome(setGameState, setRemoteNavigation, setMultiplayerState);
        }
    }
}

export function handleBoardNavigation({
    isLeft,
    isRight,
    isUp,
    isDown,
    isEnter,
    remoteNavigation,
    multiplayerState,
    gameState,
    setGameState,
    setMultiplayerState,
    setRemoteNavigation,
    onSquareClick,
    allowMove,
    setHoveredSquare,
}: HandleBoardNavigationParams) {
    if (remoteNavigation.isInGameOverMenu) return;
    if (remoteNavigation.isInMenuIcons) {
        handleMenuIconNavigation(
            isLeft,
            isUp,
            isDown,
            isEnter,
            remoteNavigation,
            gameState,
            setGameState,
            setRemoteNavigation,
        );
        return;
    }
    if (remoteNavigation.isInGameOverMenu) {
        handleGameOverMenuNavigation(
            isUp,
            isDown,
            isEnter,
            remoteNavigation,
            setGameState,
            setMultiplayerState,
            setRemoteNavigation,
        );
        return;
    }

    const prevSquare = remoteNavigation.remoteOverSquare ?? "e2";
    let nextSquare: Square | null = null;
    const isBlackPlayer = multiplayerState.playerColor === "b";

    if (isBlackPlayer && !multiplayerState.isHost) {
        if (isRight) nextSquare = getLeftSquare(prevSquare);
        if (isLeft) nextSquare = getRightSquare(prevSquare);
        if (isUp) nextSquare = getDownSquare(prevSquare);
        if (isDown) nextSquare = getUpSquare(prevSquare);
    } else {
        if (isRight) nextSquare = getRightSquare(prevSquare);
        if (isLeft) nextSquare = getLeftSquare(prevSquare);
        if (isUp) nextSquare = getUpSquare(prevSquare);
        if (isDown) nextSquare = getDownSquare(prevSquare);
    }
    const col = prevSquare.charAt(0);
    const isLeavingRightEdgeOfBoard = col === "h" && !nextSquare && isRight;

    if (nextSquare) {
        setHoveredSquare(nextSquare);
        setRemoteNavigation((prev) => ({
            ...prev,
            remoteOverSquare: nextSquare,
        }));
    }

    if (isEnter) {
        if (!allowMove()) {
            setRemoteNavigation((prev) => ({
                ...prev,
                remoteOverSquare: null,
            }));
            setHoveredSquare(null);
            return;
        }
        const targetSquare = nextSquare ?? remoteNavigation.remoteOverSquare;
        if (targetSquare) {
            setRemoteNavigation((prev) => ({
                ...prev,
                remoteClickSquare: targetSquare,
            }));
            onSquareClick(targetSquare);
        }
        return;
    }

    if (isLeavingRightEdgeOfBoard) {
        setRemoteNavigation({
            remoteClickSquare: null,
            remoteOverSquare: null,
            isInMenuIcons: true,
            selectedMenuIcon: "Options",
            isInGameOverMenu: false,
            selectedGameOverIcon: null,
        });
    }
}
